import React, { ChangeEvent, useState } from 'react';
import { SubmitButton } from '@nintendo/forms';
import { Button, Modal } from 'react-bootstrap';
import ModalConfirmCheckbox from './ModalConfirmCheckBox';
import NinModal from './NinModal';

type Props = {
    title: string;
    onConfirm: () => void;
    warningText?: string | React.ReactElement;
    body: string | React.ReactElement;
    toggleButton: React.ReactElement;
    confirmButtonText: string;
    disabled?: boolean;

    enableConfirmCheckbox?: boolean;
    confirmCheckboxText?: string | React.ReactElement;
};

/**
 * Generic component for rendering a modal to confirm deletion of a particular resource. Includes the following parameters:
 *
 * title - Title of the modal
 * onConfirm - The deletion API function
 * warningText - Warning text that brings the user's attention
 * body - Information about what will occur after deletion
 * toggleButton - Button to toggle the modal
 * confirmButtonText - The text inside the button to confirm deletion
 * disabled - Optional parameter to disable the deletion button in certain conditions
 *
 * enableConfirmCheckbox - Optional checkbox the user must select before the submit button is enabled. Setting this
 *  flag will automatically disable the submit button.
 * confirmCheckboxText - Optional string for the checkbox label. A default string value will be used if this is missing
 *  and the enableConfirmCheckbox flag is set.
 */
const DeleteConfirmationModal = (props: Props) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [isEnableSubmit, setIsEnableSubmit] = useState(
        props.disabled || props.enableConfirmCheckbox,
    );

    let checkbox: React.ReactElement;

    if (props.enableConfirmCheckbox) {
        const checkboxChanged = (event: ChangeEvent<HTMLInputElement>) => {
            setIsEnableSubmit(!event.target.checked);
        };

        checkbox = (
            <ModalConfirmCheckbox
                label={
                    props.confirmCheckboxText
                        ? props.confirmCheckboxText
                        : 'Accept and confirm delete'
                }
                onChangeCallback={checkboxChanged}
            />
        );
    }

    return (
        <NinModal
            button={props.toggleButton}
            render={(toggleModal) => (
                <>
                    <Modal.Header>{props.title}</Modal.Header>
                    <Modal.Body>
                        {error ? (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        ) : null}
                        {props.warningText ? (
                            <div className="alert alert-warning" role="alert">
                                {props.warningText}
                            </div>
                        ) : null}
                        <div>{props.body}</div>

                        <div>
                            <br />
                            {checkbox}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="button"
                            className="btn-outline-secondary mr-auto"
                            onClick={toggleModal}
                            disabled={isSubmitting}
                        >
                            Close
                        </Button>
                        <SubmitButton
                            data-testid="modal-submit-button"
                            isSubmitting={isSubmitting}
                            disabled={isEnableSubmit}
                            className="btn-danger"
                            buttonText={props.confirmButtonText}
                            onClick={async () => {
                                try {
                                    setIsSubmitting(true);
                                    await props.onConfirm();
                                    toggleModal();
                                } catch (e) {
                                    if (e instanceof Error) {
                                        setError(`Submit request failed: ${e.message}`);
                                    }
                                    setIsSubmitting(false);
                                }
                            }}
                        />
                    </Modal.Footer>
                </>
            )}
        />
    );
};

export default DeleteConfirmationModal;
